import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { isInstagramCdnAvatarUrl, isTemporaryRemoteMediaUrl } from '../../../../utils/failedImageCache';
import { ChatMessage } from '../../hooks/types';
import { MediaLightbox } from './media/MediaLightbox';

interface InstagramStoryReplyProps {
    msg: ChatMessage;
    isMe: boolean;
}

function canShowStoryMedia(url?: string): boolean {
    if (!url) return false;
    return !isTemporaryRemoteMediaUrl(url) || isInstagramCdnAvatarUrl(url);
}

function inferVisualType(url: string, type?: string): 'image' | 'video' {
    if (type === 'video' || type === 'image') return type;
    return /\.(mp4|mov|m4v|webm)(\?|$)/i.test(url) ? 'video' : 'image';
}

/** Preview de reply a Story no estilo Instagram (fora do balão). */
export const InstagramStoryReply: React.FC<InstagramStoryReplyProps> = ({
    msg,
    isMe,
}) => {
    const quote = msg.quotedMessage;
    const [thumbFailed, setThumbFailed] = React.useState(false);
    const [lightboxOpen, setLightboxOpen] = React.useState(false);

    React.useEffect(() => {
        setThumbFailed(false);
    }, [quote?.mediaUrl]);

    if (!quote?.id.startsWith('ig-story:')) return null;

    const mediaUrl = quote.mediaUrl;
    const visualType = mediaUrl ? inferVisualType(mediaUrl, quote.type) : 'image';
    const showThumb = !!mediaUrl && !thumbFailed && canShowStoryMedia(mediaUrl);
    const canOpen = showThumb;

    return (
        <>
            <div className={`mb-1.5 flex w-full items-end gap-3 ${isMe ? 'flex-row' : 'flex-row-reverse'}`}>
                <p className={`min-w-0 flex-1 self-center text-[11px] leading-snug text-muted ${isMe ? 'text-left' : 'text-right'}`}>
                    Respondeu ao Story
                </p>

                {showThumb && (
                    <button
                        type="button"
                        aria-label="Ver story"
                        onClick={() => setLightboxOpen(true)}
                        className="group relative h-[148px] w-[84px] shrink-0 overflow-hidden rounded-2xl bg-black/40 shadow-sm ring-1 ring-black/5 transition-transform active:scale-[0.98]"
                    >
                        {visualType === 'image' ? (
                            <img
                                src={mediaUrl}
                                alt=""
                                className="h-full w-full object-cover"
                                loading="lazy"
                                decoding="async"
                                referrerPolicy="no-referrer"
                                onError={() => setThumbFailed(true)}
                            />
                        ) : (
                            <video
                                src={mediaUrl}
                                className="h-full w-full object-cover"
                                muted
                                playsInline
                                preload="metadata"
                                onError={() => setThumbFailed(true)}
                            />
                        )}
                        {canOpen && (
                            <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25 opacity-90 transition-opacity group-hover:bg-black/35">
                                <AppIcon name="eye" className="h-4 w-4 text-white drop-shadow" />
                            </span>
                        )}
                    </button>
                )}
            </div>

            {lightboxOpen && mediaUrl && (
                <MediaLightbox
                    items={[{
                        url: mediaUrl,
                        type: visualType,
                    }]}
                    initialIndex={0}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </>
    );
};

export function isInstagramStoryQuote(msg: ChatMessage): boolean {
    return Boolean(msg.quotedMessage?.id?.startsWith('ig-story:'));
}
